import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "../store";
import { baseUrl } from "config/config";
import { postApiCall } from "../../services/apiHelper";
import { updatekeyProvisionGlobalData } from "./updateDataSlice";

export interface KeyAttributes {
  firmName?: string;
  keyContacts?: string;
  email?: string;
  riskRating?: number;
}

export interface JvPartnerData {
  firmName?: string;
  keyContacts?: string;
  email?: string;
  riskRating?: number;
}

export interface HoldingStructure {
  specialPurposeVehicle?: string;
  holdingCompany?: string;
}

export interface ThirdPartyInsurer {
  firmName?: string;
  keyContacts?: string;
  email?: string;
  phoneNumber?: number;
}

export interface KeyProvisionGlobalData {
  assetPlanId?: string;
  planningAdviser?: KeyAttributes;
  buildingContractors?: KeyAttributes;
  leasingAgents?: KeyAttributes;
  propertyManagingAgent?: KeyAttributes;
  operator?: KeyAttributes;
  thirdPartyAssetManager?: KeyAttributes;
  jvPartnerData?: JvPartnerData[];
  holdingStructure?: HoldingStructure;
  thirdPartyInsurer?: ThirdPartyInsurer;
}

interface InitialState {
  data: KeyProvisionGlobalData;
  error: string;
  loading: boolean;
  cacheKey: string | undefined;
  isNoData: boolean;
}

const initialState: InitialState = {
  data: {},
  error: '',
  loading: false,
  cacheKey: undefined,
  isNoData: false,
};

const keyProvisionGlobalNewSlice = createSlice({
  name: "keyProvisionGlobalNew",
  initialState,
  reducers: {
    resetState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getKeyProvisionGlobalNew.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getKeyProvisionGlobalNew.fulfilled, (state, action: PayloadAction<KeyProvisionGlobalData>) => {
      state.loading = false;
      state.data = action.payload;
      state.cacheKey = 'keyProvisionGlobalCache';
      state.isNoData = Object.keys(action.payload).length === 0;
    });
    builder.addCase(getKeyProvisionGlobalNew.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch key provision global data';
    });
  }
});

export const getKeyProvisionGlobalNew = createAsyncThunk<
  KeyProvisionGlobalData,
  any,
  { state: RootState; dispatch: AppDispatch }
>("getKeyProvisionGlobalNew", async (payload: any, { getState, rejectWithValue, dispatch }) => {
  const state = getState().keyProvisionGlobalNew;

  if (state.cacheKey === 'keyProvisionGlobalCache') {
    return state.data;
  }

  try {
    const response: any = await postApiCall(`${baseUrl}KeyProvisionsGlobal/GetKeyProvisionGlobalByAssetId?AssetId=${payload}`, payload);
    
    // Update the updateDataSlice with the fetched data
    dispatch(updatekeyProvisionGlobalData(response));
    
    return response;
  } catch (error) {
    console.error('Error fetching key provision global:', error);
    return rejectWithValue('Failed to fetch key provision global data');
  }
});

export const { resetState: resetKeyProvisionGlobalNewStore } = keyProvisionGlobalNewSlice.actions;
export default keyProvisionGlobalNewSlice.reducer;